import { ButtonGroup, FormikPasswordWithButtonInput, passwordValidation } from '@td-client-frontend/common';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PiwikAction, PiwikCategory, PiwikName } from '../@types/common';
import { trackEvent } from '../api/piwik/piwikService';
import { Button } from '../components/form-controls/Button';
import { FormikValidatePasswordInput } from '../components/Formik/FormikValidatePasswordInput';
import { useAuth } from '../context/AuthContext';
import { useAxios } from '../context/AxiosContext';

type FormValues = { newPassword: string; verifyNewPassword: string; currentPassword: string };

export function UpdatePassword() {
  const { authAxios } = useAxios();
  const { onPasswordChange } = useAuth();

  const handleSubmit = async ({ newPassword, verifyNewPassword, currentPassword }: FormValues) => {
    trackEvent({
      category: PiwikCategory.BUTTONS,
      action: PiwikAction.CLICKED,
      name: PiwikName.UPDATE_PASSWORD
    });
    try {
      await authAxios.post('/change-password', {
        confirmPassword: verifyNewPassword,
        newPassword,
        password: currentPassword
      });
      toast.success('Your password has been changed', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
        autoClose: 10000
      });

      onPasswordChange();
    } catch (err) {
      toast.warning('We were unable to update your password, please verify your information and try again', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
        autoClose: 10000
      });
    }
  };

  return (
    <div className="flex flex-col justify-start w-full h-full max-w-2xl mx-auto font-effra">
      <Formik
        initialValues={
          {
            currentPassword: '',
            newPassword: '',
            verifyNewPassword: ''
          } as FormValues
        }
        enableReinitialize
        validationSchema={Yup.object({
          currentPassword: Yup.string().required('Required'),
          newPassword: passwordValidation,
          verifyNewPassword: passwordValidation.oneOf([Yup.ref('newPassword'), ''], 'Passwords must match')
        })}
        onSubmit={async (values, { setSubmitting }: FormikHelpers<FormValues>) => {
          setSubmitting(true);
          await handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, isValid }) => (
          <>
            <h2 className="m-6 text-4xl font-medium leading-tight font-effra">Update Password</h2>
            <Form
              data-testid="reset-pass-form"
              className="flex flex-col items-center p-8 bg-white shadow-lg rounded-lg"
            >
              <div className="w-full max-w-lg px-3 py-2 my-4 border rounded-lg border-warning bg-mango-500">
                <h4>Please create a new password for your account</h4>
              </div>
              <div className="w-full max-w-lg">
                <FormikPasswordWithButtonInput
                  label="Current Password"
                  isRequired
                  placeholder="Please enter current password"
                  data-testid="current-password"
                  name="currentPassword"
                  autoComplete="current-password"
                />
                <FormikValidatePasswordInput
                  label="New Password"
                  isRequired
                  placeholder="Please enter password"
                  data-testid="new-password"
                  name="newPassword"
                  autoComplete="new-password"
                />
                <FormikPasswordWithButtonInput
                  label="Verify New Password"
                  isRequired
                  placeholder="Please enter password again"
                  data-testid="verify-new-password"
                  name="verifyNewPassword"
                  showFieldError
                  autoComplete="new-password"
                />
              </div>
              <div className="w-full max-w-lg">
                <ButtonGroup>
                  <Button data-testid="submitButton" disabled={isSubmitting || !isValid} kind="primary" type="submit">
                    Update Password
                  </Button>
                </ButtonGroup>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}

export default UpdatePassword;
